/**
 * ExcelController — context surface the dashboard provider implements
 * so each ExcelWidget instance can mutate its own config (cell content,
 * resize) and request re-evaluation in Phase 3.
 */

import React, { createContext, useContext, useMemo } from 'react';

import type { CfRule } from './conditionalFormatting';
import type { CellCoord, ExcelCell, ExcelCellNumberFormat } from './excelConfig';

export interface ExcelController {
  setCellRaw: (r: number, c: number, raw: string) => void;
  setResolvedDsl: (r: number, c: number, dsl: string) => void;
  /** Clear `raw` for each cell in one batched config update. */
  clearCells: (coords: CellCoord[]) => void;
  /**
   * Apply a partial style patch (textColor / bgColor) to each cell in
   * `coords` in a single batched update. Pass `null` to clear a key.
   */
  setCellsStyle: (
    coords: CellCoord[],
    style: { textColor?: string | null; bgColor?: string | null },
  ) => void;
  /**
   * Set (or clear, with `null`) the `numberFormat` on every cell in
   * `coords` in a single batched update.
   */
  setCellsFormat: (
    coords: CellCoord[],
    format: ExcelCellNumberFormat | null,
  ) => void;
  /**
   * Set (or clear, with `null` / `[]`) `conditionalFormatting` on every
   * cell in `coords` in a single batched update.
   */
  setCellsConditionalFormatting: (
    coords: CellCoord[],
    rules: CfRule[] | null,
  ) => void;
  /** Replace the whole sheet with `cells`, written row-major from A1. */
  writeSheetCells: (cells: ExcelCell[][]) => void;
  /**
   * Write an A1-keyed patch of raw cell values in one batched update.
   * `grid` grows the sheet first, so a patch may address cells that do
   * not exist yet. Throws when an address is malformed or still falls
   * outside the grid afterwards.
   */
  applyCellPatch: (
    patch: Record<string, string>,
    grid?: { rows: number; cols: number },
  ) => void;
  addRow: () => void;
  removeRow: () => void;
  addCol: () => void;
  removeCol: () => void;
  /** Stage-2 re-evaluation (no LLM). Bumps the refresh epoch. */
  requestRefresh: () => void;
  /** Stage-1 + stage-2 re-evaluation. Bumps the retranslate epoch. */
  requestRetranslate: () => void;
  /** Reactive epochs the evaluator hook subscribes to. */
  refreshEpoch: number;
  retranslateEpoch: number;
  /**
   * Spawn a chart widget below the excel widget with one trace per DSL
   * cell in `coords`. Literal cells, empty `=` cells, and out-of-bounds
   * coords are silently dropped. No-op when no DSL cell remains after
   * filtering.
   */
  chartCells: (coords: CellCoord[]) => void;
  /** Pop the per-instance undo stack and restore the previous config. */
  undo: () => void;
  /** Whether `undo()` would do anything (for disabling the toolbar button). */
  canUndo: boolean;
  /** Whether this widget is frozen on the canvas. */
  pinned: boolean;
  togglePin: () => void;
  remove: () => void;
}

export interface ExcelControllerProvider {
  getController: (instanceId: string) => ExcelController;
}

const ExcelControllerContext = createContext<ExcelControllerProvider | null>(null);

export const ExcelControllerProviderContext = ExcelControllerContext.Provider;

export function useExcelController(instanceId: string): ExcelController {
  const provider = useContext(ExcelControllerContext);
  if (!provider) {
    throw new Error(
      'useExcelController must be used inside an ExcelControllerProviderContext (MarketMonitorDashboardProvider).',
    );
  }
  return useMemo(() => provider.getController(instanceId), [provider, instanceId]);
}
